import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import type { Query } from '@/api/query'
import { useMutation, useQuery } from '@/api/query'
import { downloadCsv } from '@/lib/csv'
import { formatDayMonth } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import {
  CHANNEL_CHIP,
  CONTACT_LABELS,
  DELIVERY_LABELS,
  IST,
  STAGE_LABELS,
  STAGE_TONES,
  formatScore,
} from './stages'

type Lead = Schema<'LeadOut'>
type LeadsQuery = Query<Schema<'LeadListResponse'>>

/** `DEFAULT_PAGE` in `app/v1/leads.py`, mirrored — OpenAPI describes a
 *  parameter's bounds and not its default. */
const PAGE = 25

/** Indian digit grouping — *1,284*, not *1284* and not *1,284* the American way
 *  once the numbers get past a lakh. The product is India-facing (CLAUDE.md), and
 *  a lead count is one of the few figures on these screens big enough to need
 *  grouping at all. */
const GROUPED = new Intl.NumberFormat('en-IN')

/** The stage filter, in §5's pipeline order. `lost` is last because it is the
 *  one a tenant reaches for deliberately rather than while scanning. */
const STAGES: readonly { value: string; label: string }[] = [
  { value: 'found', label: 'Found' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'replied', label: 'Replied' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

/** Which channels a lead can be reached on *by this product* —
 *  `app/outreach/channels.SENDABLE`. `sms` and `rcs` came out on 2026-09-07 with
 *  the owner's decision to ship outreach on email and WhatsApp only, and `ivr`
 *  earlier when AugStash deleted it. A lead found before that may still carry a
 *  message on one of them, which is why the filter is narrower than the data. */
const CHANNELS: readonly { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'linkedin', label: 'LinkedIn' },
]

/** The two orderings the API can page. */
type Order = 'recent' | 'score'

/**
 * The leads list — `design/screens/outreach.html#leads`, audit row §B1.
 *
 * S-B13 ported `../linkmailer`'s discovery libraries and recorded leads from the
 * first scrape onward; nothing has ever shown them. This is that screen, and
 * `#lead` beside it is where one of these rows opens.
 *
 * WHICH HEADERS SORT, AND WHY THE OTHER FIVE DO NOT
 * ---------------------------------------------------
 * The prototype's table sorts on Name, Company, Stage and Score by clicking a
 * header, which is a client-side sort of the twenty-five rows the client happens
 * to hold. This list is keyset-paginated (standards §4 forbids offset), so a sort
 * is a *server* ordering with a cursor shaped for it — and the API has exactly
 * two: newest-first and best-score-first.
 *
 * OUTR-16 is closed by giving those two orderings to the two headers that express
 * them — **Score** and **Found** — and leaving the rest as plain `<th>`s. A header
 * that sorted one page and lied about the other fifty is worse than a header that
 * does not sort, and *find this person* — which is what sorting by name is for —
 * is what the search box does, across every page rather than across the page in
 * hand. Both orderings are descending because both cursors are: there is no
 * "worst score first", so the control never claims one.
 *
 * WHY THE COUNT IS "IN TOTAL" AND NEVER THE NUMBER OF MATCHES
 * -------------------------------------------------------------
 * `LeadListResponse.total` is `OutreachTransaction.lead_count`, which counts the
 * whole table and takes no filter — the same shape the suppression list has, and
 * the same trap: a filtered list beside an unfiltered count reads as though the
 * count described the filter. The toolbar labels it, and says how many are on
 * screen separately.
 *
 * WHY THE STAGE FILTER LIVES IN THE URL
 * ---------------------------------------
 * `Pipeline.tsx` links a column's *"N more"* at `/outreach/leads?stage=contacted`
 * and this screen read the query string nowhere, so every one of those links
 * arrived showing all five stages — the parity review's own finding against
 * `Pipeline.tsx:117`. The filter is therefore held in the URL rather than beside
 * it: one source of truth, a link that survives a reload, and no second copy in
 * component state to drift from the address bar.
 */
export function Leads(): JSX.Element {
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [channel, setChannel] = useState('')
  const [order, setOrder] = useState<Order>('recent')
  // The cursor stack, so *Previous* is a pop rather than a second cursor
  // direction the API would have to grow. `null` at the head is the first page.
  const [cursors, setCursors] = useState<(string | null)[]>([null])

  const stage = params.get('stage') ?? ''
  const cursor = cursors[cursors.length - 1] ?? null
  const filtered = search !== '' || stage !== '' || channel !== ''

  // Not debounced, for the reason `InboxPage` states: `useQuery` aborts the
  // previous request on every change, so a fast typist produces one in-flight
  // request rather than a queue of them.
  const list = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/leads', {
          params: {
            query: {
              limit: PAGE,
              order,
              cursor,
              stage: stage || null,
              channel: channel || null,
              search: search || null,
            },
          },
          signal,
        }),
      ),
    [order, cursor, stage, channel, search],
  )

  const reset = (): void => {
    setCursors([null])
  }

  // `replace` because a filter is not a place: a tenant who tried three stages
  // and pressed Back expects the screen they came from, not the second of the
  // three.
  const onStage = (value: string): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === '') next.delete('stage')
        else next.set('stage', value)
        return next
      },
      { replace: true },
    )
    reset()
  }

  const clearFilters = (): void => {
    setSearch('')
    setChannel('')
    onStage('')
  }

  const sortBy = (value: Order): void => {
    setOrder(value)
    reset()
  }

  /**
   * OUTR-14 — the current filter as a file.
   *
   * **The server renders it, not this screen**, which is the difference between
   * this button and the two `lib/csv.ts` already serves on the SEO screens: those
   * export the rows the client is holding, and this list holds twenty-five of a
   * possible several thousand. A button labelled *Export CSV* that silently wrote
   * one page would be the worst kind of working feature.
   *
   * The text still goes out through `downloadCsv`, so the file carries the BOM
   * every other export in this app carries — without it Excel on Windows reads a
   * UTF-8 CSV as the local code page, and a Telugu or Hindi prospect's name
   * arrives as mojibake for exactly the tenants this product is sold to.
   */
  const download = useMutation(() =>
    call(() =>
      api.GET('/api/v1/outreach/leads/export', {
        params: {
          query: {
            order,
            stage: stage || null,
            channel: channel || null,
            search: search || null,
          },
        },
        // The route answers `text/csv`, and openapi-fetch parses as JSON unless
        // told otherwise — which would fail on the first row of a valid file.
        parseAs: 'text',
      }),
    ),
  )

  const onExport = (): void => {
    void (async () => {
      const result = await download.run(undefined)
      // `parseAs: 'text'` types the body `unknown`, because the schema declares
      // `text/csv` with no shape. The guard is what turns that back into a string
      // without asserting one.
      if (result.ok && typeof result.data === 'string') {
        downloadCsv(`augrank-leads-${new Date().toISOString().slice(0, 10)}.csv`, result.data)
      }
    })()
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row app-leads__head">
        <h1 className="ar-h1">Leads</h1>
        <div className="ar-btn-group">
          <ExportButton busy={download.pending} onExport={onExport} />
          <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/outreach/packs">
            Find more leads
          </Link>
        </div>
      </div>

      {/* Under the header rather than inside the button group: `.ar-btn-group`
          is an `inline-flex` row, and `InlineError` renders a full
          `.ar-alert--danger` block — as a flex item beside the two buttons it
          pushed *Find more leads* onto its own line and squeezed the sentence
          into whatever was left. */}
      <InlineError failure={download.state.failure} />

      <div className="ar-card">
        <div className="ar-table-toolbar">
          <input
            className="ar-input"
            type="search"
            placeholder="Search name, company, title…"
            aria-label="Search leads"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              reset()
            }}
          />
          <select
            className="ar-select app-leads__filter"
            aria-label="Filter by stage"
            value={stage}
            onChange={(event) => {
              onStage(event.target.value)
            }}
          >
            <option value="">All stages</option>
            {STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="ar-select app-leads__filter"
            aria-label="Filter by channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value)
              reset()
            }}
          >
            <option value="">All channels</option>
            {CHANNELS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="ar-card__spacer" />
          {/* *1,284 leads* (P-LEADS-03): the total, the way the mockup counts. */}
          <span className="ar-subtle app-leads__count">
            {list.state.status === 'ready'
              ? `${GROUPED.format(list.state.data.total)} ${list.state.data.total === 1 ? 'lead' : 'leads'}`
              : null}
          </span>
        </div>

        <Body
          list={list}
          order={order}
          onSort={sortBy}
          filtered={filtered}
          onClear={clearFilters}
        />

        {/* No pager under an empty list: *Page 1 · Previous · Next* over
            *No leads here yet* offered two dead buttons and a page number for
            nothing (walkthrough 2026-09-12). Kept while filters hide rows,
            since a later page may still hold matches. */}
        {list.state.status === 'ready' &&
        list.state.data.leads.length === 0 &&
        cursors.length === 1 &&
        list.state.data.next_cursor === null ? null : (
          <div className="ar-pagination">
            {/* *Showing 1–6 of 8* (P-LEADS-05): the page's span, from the
                page index and the size, against the total. */}
            <span className="ar-pagination__info" aria-live="polite">
              {list.state.status === 'ready' && list.state.data.leads.length > 0
                ? `Showing ${String((cursors.length - 1) * PAGE + 1)}–${String(
                    (cursors.length - 1) * PAGE + list.state.data.leads.length,
                  )} of ${GROUPED.format(list.state.data.total)}`
                : ''}
            </span>
            <Button
              size="sm"
              disabled={cursors.length === 1}
              onClick={() => {
                setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))
              }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={list.state.status !== 'ready' || list.state.data.next_cursor === null}
              onClick={() => {
                if (list.state.status !== 'ready' || list.state.data.next_cursor === null) return
                const next: string | null = list.state.data.next_cursor ?? null
                setCursors((stack) => [...stack, next])
              }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function Body({
  list,
  order,
  onSort,
  filtered,
  onClear,
}: {
  list: LeadsQuery
  order: Order
  onSort: (order: Order) => void
  filtered: boolean
  onClear: () => void
}): JSX.Element {
  if (list.state.status === 'loading') return <Loading label="Loading leads" lines={6} />
  if (list.state.status === 'error')
    return (
      <div className="ar-card__body">
        <ErrorState
          failure={list.state.failure}
          onRetry={list.refetch}
          title="Could not load your leads"
        />
      </div>
    )
  if (list.state.data.leads.length === 0)
    return (
      <div className="ar-card__body">
        {/* OUTR-17. "No leads here yet" is true of an empty organisation and
            false of a filter that matched nothing — and told to somebody three
            filters deep it reads as *your leads are gone*. The two states say
            different things, and the filtered one offers the way out. */}
        {filtered ? (
          <Empty
            icon="⌕"
            title="No leads match"
            body="Try a shorter search, or clear the filters."
            action={
              <Button size="sm" onClick={onClear}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <Empty
            icon="⌕"
            title="No leads here yet"
            body="Leads arrive from the post searches your browser extension runs. Once one lands, this is where it appears — with the post it came from and why it scored the way it did."
          />
        )}
      </div>
    )

  return (
    <div className="ar-table-wrap" tabIndex={0}>
      <table className="ar-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Title</th>
            <th>Channel</th>
            <th>Stage</th>
            <SortHeader
              label="Score"
              numeric
              active={order === 'score'}
              onSort={() => {
                onSort('score')
              }}
            />
            <SortHeader
              label="Found"
              active={order === 'recent'}
              onSort={() => {
                onSort('recent')
              }}
            />
          </tr>
        </thead>
        <tbody>
          {list.state.data.leads.map((lead) => (
            <Row key={lead.id} lead={lead} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * OUTR-14 — the current filter as a file.
 *
 * **The server renders it, not this component**, which is the difference between
 * this button and the two `lib/csv.ts` already serves on the SEO screens: those
 * export the rows the client is holding, and this list holds twenty-five of a
 * possible several thousand. A button labelled *Export CSV* that silently wrote
 * one page would be the worst kind of working feature.
 *
 * The text still goes out through `downloadCsv`, so the file carries the BOM
 * every other export in this app carries — without it Excel on Windows reads a
 * UTF-8 CSV as the local code page, and a Telugu or Hindi prospect's name
 * arrives as mojibake for exactly the tenants this product is sold to.
 */
function ExportButton({ busy, onExport }: { busy: boolean; onExport: () => void }): JSX.Element {
  return (
    <Button size="sm" busy={busy} busyLabel="Preparing your file" onClick={onExport}>
      Export CSV
    </Button>
  )
}

/**
 * One header that reorders the list on the server.
 *
 * **`aria-sort` is on the `<th>`, and repeated on the button** — `Keywords.tsx`'s
 * pattern, and it is on the cell because that is the only place a screen reader
 * reads it from: on a `role="button"` the attribute is ignored, so a first
 * version that put it only there announced no sort state at all while passing a
 * test that asserted the attribute existed. The button keeps its copy because the
 * Atlas layer colours the arrow off `.ar-sort[aria-sort=…]` (`components.css:400`).
 *
 * Only *descending* is ever announced: both cursors walk one way, so claiming an
 * ascending state the API cannot produce would be the control lying about itself.
 */
function SortHeader({
  label,
  active,
  numeric = false,
  onSort,
}: {
  label: string
  active: boolean
  numeric?: boolean
  onSort: () => void
}): JSX.Element {
  const state = active ? 'descending' : 'none'
  return (
    <th className={numeric ? 'ar-num' : undefined} aria-sort={state}>
      <button type="button" className="ar-sort" aria-sort={state} onClick={onSort}>
        {label}{' '}
        <span className="ar-sort__arrow" aria-hidden="true">
          {active ? '↓' : '↕'}
        </span>
      </button>
    </th>
  )
}

function Row({ lead }: { lead: Lead }): JSX.Element {
  return (
    <tr>
      {/* Bold body text, no link colour (P-LEADS-01) — the row still leads to
          the lead. `nowrap` keeps a two-word name on one line (P-LEADS-06). */}
      <td style={{ whiteSpace: 'nowrap' }}>
        <Link className="app-leads__name" to={`/outreach/leads/${lead.id}`}>
          <strong>{lead.full_name ?? 'Unnamed lead'}</strong>
        </Link>
      </td>
      {/* `company` holds the headline when the capture could not split one
          from the other; a company equal to the headline is printed once. */}
      <td>{lead.company === lead.headline ? '—' : (lead.company ?? '—')}</td>
      <td>{lead.headline ?? '—'}</td>
      <td>
        <ChannelCell lead={lead} />
      </td>
      <td>
        <span className={`ar-badge ar-badge--${STAGE_TONES[lead.stage] ?? 'neutral'}`}>
          {STAGE_LABELS[lead.stage] ?? lead.stage}
        </span>
        {/*
          OUTR-18. The mockup's red "Bounced" badge, beside the stage rather than
          instead of it: a lead whose email bounced is still at whatever stage
          they were, and this says the address is unreachable, not that the
          pipeline moved. Absent when everything sent arrived — and for a lead
          nobody has written to yet, which is the same answer.
        */}
        {lead.delivery_state ? (
          <span
            className="ar-badge ar-badge--danger"
            style={{ marginLeft: 'var(--ar-space-2)' }}
            title="The last message to this lead did not arrive"
          >
            {DELIVERY_LABELS[lead.delivery_state] ?? 'Not delivered'}
          </span>
        ) : null}
      </td>
      <td className="ar-num">{formatScore(lead.relevance_score ?? null)}</td>
      <td className="ar-muted" style={{ whiteSpace: 'nowrap' }}>
        {formatDayMonth(lead.first_seen_at, IST)}
      </td>
    </tr>
  )
}

/**
 * The channel column, and it says *how many* addresses rather than only which.
 *
 * The prototype shows one channel per lead. `contact_count` is on the response
 * because one is often not the whole truth — a lead who published an email and a
 * mobile is reachable on both, and the ladder's *Switch channel* rung is the
 * whole reason the second one matters. Showing only the first would make that
 * rung look like it came from nowhere.
 *
 * OUTR-55: the kind is a chip rather than a word, the same chip the inbox draws
 * on a message, so *which channel* reads the same colour everywhere in the
 * module.
 */
function ChannelCell({ lead }: { lead: Lead }): JSX.Element {
  const kind = lead.contact_kind ?? null
  if (kind === null)
    return (
      <span className="ar-subtle" title="No address was found in this lead's post">
        No address
      </span>
    )
  const extra = lead.contact_count - 1
  return (
    <span className="ar-row app-leads__chan">
      <span className={`app-chan ${CHANNEL_CHIP[kind] ?? ''}`}>{CONTACT_LABELS[kind] ?? kind}</span>
      {extra > 0 ? <span className="ar-subtle">+{extra}</span> : null}
    </span>
  )
}
